import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Select, Input, Button, Space, Card, Upload, message, Popconfirm } from 'antd';
import { EvidenceTypeOptions, toSelectOptions } from '../../../../constants/debtEnums';
import { uploadBusinessFile, deleteBusinessFile } from '../../../../services/debt';

const EvidenceStep = ({ value, onChange, onNext, onPrev, debtId, ensureDebtId }) => {
  const [form] = Form.useForm();
  const [deletingFileIdx, setDeletingFileIdx] = useState(null);
  const [deletingItemIdx, setDeletingItemIdx] = useState(null);
  const [deleteErrors, setDeleteErrors] = useState({});
  const { user } = useSelector(state => state.auth || {});

  useEffect(() => {
    form.setFieldsValue({ evidences: value.evidences });
  }, [value.evidences, form]);

  const handleValuesChange = (_, allValues) => {
    onChange(prev => ({ ...prev, evidences: allValues.evidences || [] }));
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      const evidences = form.getFieldValue('evidences') || [];
      if (evidences.length < 1) {
        throw new Error('请至少添加一条证据材料');
      }
      onNext();
    } catch (e) {
      if (e?.message) {
        message.error(e.message);
      }
    }
  };

  const fileNameFromKey = (key) => {
    if (!key) return '';
    try {
      return String(key).substring(String(key).lastIndexOf('/') + 1);
    } catch (_) {
      return String(key);
    }
  };

  const uploadProps = (fieldName, index) => ({
    name: 'file',
    accept: '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const userId = user?.id;
        if (!userId) {
          message.error('未登录或用户信息缺失');
          onError && onError(new Error('用户未登录'));
          return;
        }
        const { data } = await uploadBusinessFile(file, 'debt_evidence_temp', userId);
        if (data?.objectKey) {
          const list = form.getFieldValue('evidences') || [];
          const next = [...list];
          next[index] = { 
            ...(next[index] || {}), 
            file_url: data.objectKey,
            preview_url: data.url
          };
          form.setFieldsValue({ evidences: next });
          onChange(prev => ({ ...prev, evidences: next }));
          message.success('文件上传成功');
        }
        onSuccess && onSuccess(data);
      } catch (err) {
        message.error('文件上传失败');
        onError && onError(err);
      }
    },
  });

  const handleRemoveEvidence = async (name, remove) => {
    try {
      setDeletingItemIdx(name);
      const list = form.getFieldValue('evidences') || [];
      const item = list[name] || {};
      if (item.file_url) {
        const isTemp = String(item.file_url || '').includes('/debt_evidence_temp/');
        const businessType = isTemp ? 'debt_evidence_temp' : 'debt_evidence';
        const ensuredId = isTemp ? (user?.id) : (debtId || await ensureDebtId());
        await deleteBusinessFile(item.file_url, businessType, ensuredId);
      }
    } catch (err) {
    }
    const next = [...(form.getFieldValue('evidences') || [])];
    next.splice(name, 1);
    form.setFieldsValue({ evidences: next });
    onChange(prev => ({ ...prev, evidences: next }));
    remove(name);
    message.success('已删除证据项');
    setDeletingItemIdx(null);
  };

  const handleDeleteFile = async (index) => {
    try {
      setDeletingFileIdx(index);
      setDeleteErrors(prev => ({ ...prev, [index]: undefined }));
      const userId = user?.id;
      if (!userId) {
        message.error('未登录或用户信息缺失');
        setDeletingFileIdx(null);
        return;
      }
      const list = form.getFieldValue('evidences') || [];
      const item = list[index] || {};
      const url = item.file_url;
      if (!url) {
        message.error('暂无可删除的文件');
        setDeletingFileIdx(null);
        return;
      }
      const isTemp = String(url).includes('/debt_evidence_temp/');
      const businessType = isTemp ? 'debt_evidence_temp' : 'debt_evidence';
      const bId = isTemp ? userId : (debtId || await ensureDebtId());
      await deleteBusinessFile(url, businessType, bId);
      const next = [...list];
      next[index] = { ...(next[index] || {}), file_id: undefined, file_name: undefined, file_url: undefined };
      form.setFieldsValue({ evidences: next });
      onChange(prev => ({ ...prev, evidences: next }));
      message.success('已删除文件');
      setDeletingFileIdx(null);
    } catch (err) {
      message.error('删除文件失败');
      setDeleteErrors(prev => ({ ...prev, [index]: (err?.message || '删除文件失败') }));
      setDeletingFileIdx(null);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ evidences: value.evidences }}
      onValuesChange={handleValuesChange}
    >
      <Card size="small" title="证据材料">
        <Form.List name="evidences">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} className="evidence-card" size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <Form.Item {...restField} name={[name, 'evidence_type']} label="证据类型" rules={[{ required: true, message: '请选择证据类型' }]}> 
                      <Select style={{ width: 240 }} options={toSelectOptions(EvidenceTypeOptions)} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'file_url']} hidden rules={[{ required: true, message: '请上传证据文件' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="文件名称URL"> 
                      <Input readOnly style={{ width: 200 }} value={fileNameFromKey(form.getFieldValue(['evidences', name, 'file_url']))} placeholder="上传后自动填充" />
                    </Form.Item>
                    <Upload {...uploadProps('file_id', name)}>
                      <Button type="primary">上传证据</Button>
                    </Upload>
                    <Popconfirm title="确认删除该文件？" onConfirm={() => handleDeleteFile(name)} disabled={!form.getFieldValue(['evidences', name, 'file_url'])}>
                      <Button danger disabled={!form.getFieldValue(['evidences', name, 'file_url'])} loading={deletingFileIdx===name}>删除文件</Button>
                    </Popconfirm>
                    {form.getFieldValue(['evidences', name, 'preview_url']) && (
                      <a href={form.getFieldValue(['evidences', name, 'preview_url'])} target="_blank" rel="noreferrer">预览</a>
                    )}
                    {deleteErrors[name] && (
                      <Button type="link" onClick={() => handleDeleteFile(name)}>重试</Button>
                    )}
                    <Form.Item {...restField} name={[name, 'remark']} label="备注">
                      <Input style={{ width: 320 }} />
                    </Form.Item>
                  </Space>
                  <div style={{ textAlign: 'right' }}>
                    <Popconfirm title="确认删除该证据项？" onConfirm={() => handleRemoveEvidence(name, remove)}>
                      <Button danger type="link" loading={deletingItemIdx===name}>删除证据项</Button>
                    </Popconfirm>
                  </div>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()} block>
                添加证据材料
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Form.Item style={{ marginTop: 16 }}>
        <Space>
          <Button onClick={onPrev}>上一步</Button>
          <Button type="primary" onClick={handleNext}>下一步</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default EvidenceStep;
